import React, { useState } from 'react'
import { WeeklyEntries, TimeEntry } from '../../types'
import { timeUtils } from '../../services/timerService'
import { 
  Clock, 
  ChevronDown, 
  ChevronRight, 
  Lock,
  Edit,
  Trash2,
  StopCircle,
  MoreVertical,
  PlusCircle,
  Calendar
} from 'lucide-react'
import Tooltip from '../common/Tooltip'

interface WeeklyTimeEntryListProps {
  weeklyEntries: WeeklyEntries
  onEdit?: (entry: TimeEntry, originalDate?: string) => void
  onDelete?: (entryId: string) => void
  onStop?: () => void
  className?: string
  disabled?: boolean
}

const WeeklyTimeEntryList: React.FC<WeeklyTimeEntryListProps> = ({
  weeklyEntries,
  onEdit,
  onDelete,
  onStop,
  className = '',
  disabled = false
}) => {
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set())

  // 將日期按倒序排列（最新的在上面）
  const sortedDates = Object.keys(weeklyEntries).sort((a, b) => new Date(b).getTime() - new Date(a).getTime())

  // 預設展開今天和昨天
  React.useEffect(() => {
    const today = new Date().toISOString().split('T')[0]
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    setExpandedDates(new Set([today, yesterday]))
  }, [])

  const toggleDateExpansion = (date: string) => {
    const newExpanded = new Set(expandedDates)
    if (newExpanded.has(date)) {
      newExpanded.delete(date)
    } else {
      newExpanded.add(date)
    }
    setExpandedDates(newExpanded)
  }

  const formatDateHeader = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
    
    if (date.toDateString() === today.toDateString()) {
      return '今天'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return '昨天'
    } else {
      return date.toLocaleDateString('zh-TW', {
        month: '2-digit',
        day: '2-digit',
        weekday: 'short'
      })
    }
  }

  if (sortedDates.length === 0) {
    return (
      <div className={`${className}`}>
        <div className="empty-state">
          <div className="empty-state-icon">
            <Calendar className="w-10 h-10" />
          </div>
          <h4 className="empty-state-title">本週尚無記錄</h4>
          <p className="empty-state-desc">
            選擇專案並開始計時，建立第一筆工時記錄
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {sortedDates.map((date, dateIndex) => {
        const dayData = weeklyEntries[date]
        const isExpanded = expandedDates.has(date)
        const isSubmitted = dayData.isSubmitted
        const canEdit = dayData.canEdit && !disabled

        return (
          <div
            key={date}
            className={`
              glass-card rounded-xl overflow-hidden transition-all duration-300 animate-fade-in
              ${isSubmitted 
                ? 'bg-secondary-50/80 border-secondary-200/50' 
                : 'hover:shadow-lg'
              }
            `}
            style={{ animationDelay: `${dateIndex * 50}ms` }}
          >
            {/* 日期標題 */}
            <div
              onClick={() => toggleDateExpansion(date)}
              className={`
                flex items-center justify-between p-4 cursor-pointer
                transition-colors duration-200
                ${isSubmitted ? 'hover:bg-secondary-100/50' : 'hover:bg-primary-50/30'}
              `}
            >
              <div className="flex items-center gap-3">
                {/* 展開/收起圖示 */}
                <div className={`
                  flex items-center justify-center w-6 h-6 rounded-lg
                  transition-all duration-200
                  ${isExpanded ? 'bg-primary-100 text-primary-600' : 'bg-secondary-100 text-secondary-500'}
                `}>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </div>
                
                {/* 日期文字 */}
                <div>
                  <span className={`
                    font-semibold
                    ${isSubmitted ? 'text-secondary-600' : 'text-secondary-800'}
                  `}>
                    {formatDateHeader(date)}
                  </span>
                  <span className="ml-2 text-xs text-secondary-500">
                    {date.split('-').slice(1).join('/')}
                  </span>
                </div>

                {/* 鎖定狀態 */}
                {isSubmitted && (
                  <Tooltip content="此日期已提交，無法編輯">
                    <div className="flex items-center gap-1 px-2 py-1 rounded-full 
                      bg-secondary-200/50 text-secondary-500 text-xs">
                      <Lock className="w-3 h-3" />
                      <span>已提交</span>
                    </div>
                  </Tooltip>
                )}
              </div>

              {/* 統計資訊 */}
              <div className="flex items-center gap-4">
                <div className={`
                  text-right
                  ${isSubmitted ? 'text-secondary-500' : 'text-secondary-700'}
                `}>
                  <div className="font-mono font-bold text-lg">
                    {timeUtils.formatDuration(dayData.totalDuration)}
                  </div>
                  <div className="text-xs text-secondary-400">
                    {dayData.entries.length} 筆記錄
                  </div>
                </div>
              </div>
            </div>

            {/* 記錄列表 */}
            {isExpanded && (
              <div className="border-t border-secondary-200/50">
                {dayData.entries.length === 0 ? (
                  <div className="p-6 text-center text-secondary-500">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-secondary-300" />
                    <p>此日期無記錄</p>
                  </div>
                ) : (
                  <div className="divide-y divide-secondary-100/50">
                    {dayData.entries.map((entry, entryIndex) => (
                      <WeeklyTimeEntryItem
                        key={entry.id}
                        entry={entry}
                        onEdit={canEdit ? onEdit : undefined}
                        onDelete={canEdit ? onDelete : undefined}
                        onStop={onStop}
                        disabled={!canEdit}
                        isSubmitted={isSubmitted}
                        originalDate={date}
                        index={entryIndex}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// 單筆週記錄項目組件
interface WeeklyTimeEntryItemProps {
  entry: TimeEntry
  onEdit?: (entry: TimeEntry, originalDate?: string) => void
  onDelete?: (entryId: string) => void
  onStop?: () => void
  disabled?: boolean
  isSubmitted?: boolean
  originalDate?: string
  index?: number
}

const WeeklyTimeEntryItem: React.FC<WeeklyTimeEntryItemProps> = ({
  entry,
  onEdit,
  onDelete,
  onStop,
  disabled = false,
  isSubmitted = false,
  originalDate,
  index = 0
}) => {
  const [showActions, setShowActions] = useState(false)
  const isRunning = entry.status === 'RUNNING'
  const duration = isRunning 
    ? timeUtils.calculateDuration(entry.startTime)
    : entry.duration

  return (
    <div 
      className={`
        p-4 transition-all duration-200 animate-fade-in
        ${isSubmitted 
          ? 'bg-secondary-50/30' 
          : 'hover:bg-primary-50/20'
        }
        ${isRunning ? 'bg-green-50/30' : ''}
      `}
      style={{ animationDelay: `${index * 30}ms` }}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="flex items-start gap-4">
        {/* 專案顏色標識 */}
        <div className="flex-shrink-0 pt-1">
          <div 
            className="w-3 h-3 rounded-full ring-2 ring-white shadow-sm"
            style={{ backgroundColor: entry.project.color }}
          />
        </div>
        
        {/* 內容區 */}
        <div className="flex-1 min-w-0">
          {/* 專案名稱和標籤 */}
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`
              text-sm font-semibold truncate
              ${isSubmitted ? 'text-secondary-600' : 'text-secondary-800'}
            `}>
              {entry.project.name}
            </span>
            
            {isRunning && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full 
                text-xs font-medium bg-green-100 text-green-700">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                進行中
              </span>
            )}
            
            {entry.isManual && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full 
                text-xs font-medium bg-orange-100 text-orange-700">
                <PlusCircle className="w-2.5 h-2.5" />
                手動
              </span>
            )}
            
            {entry.isEdited && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full 
                text-xs font-medium bg-blue-100 text-blue-700">
                <Edit className="w-2.5 h-2.5" />
                已編輯
              </span>
            )}
          </div>
          
          {/* 描述 */}
          {entry.description && (
            <p className={`
              text-sm mb-2 line-clamp-2
              ${isSubmitted ? 'text-secondary-500' : 'text-secondary-600'}
            `}>
              {entry.description}
            </p>
          )}
          {/* 手動新增原因 */}
          {entry.isManual && entry.manualReason && (
            <p className="text-xs text-amber-600 mb-2">
              手動新增原因：{entry.manualReason}
            </p>
          )}
          
          {/* 時間資訊 */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-secondary-500">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {timeUtils.formatTime(entry.startTime)}
            </span>
            {entry.endTime ? (
              <>
                <span>→</span>
                <span>{timeUtils.formatTime(entry.endTime)}</span>
              </>
            ) : (
              <span className="text-green-600 font-medium">進行中...</span>
            )}
          </div>
        </div>
        
        {/* 時長和操作 */}
        <div className="flex items-center gap-3">
          <div className={`
            px-3 py-1.5 rounded-lg font-mono font-bold
            ${isRunning 
              ? 'bg-green-50 text-green-600' 
              : isSubmitted 
                ? 'bg-secondary-100/50 text-secondary-500' 
                : 'bg-secondary-50 text-secondary-700'
            }
          `}>
            {timeUtils.formatDuration(duration)}
          </div>
          
          {!disabled && (showActions || isRunning) && (
            <ActionButtons
              entry={entry}
              onEdit={onEdit}
              onDelete={onDelete}
              onStop={onStop}
              isSubmitted={isSubmitted}
              originalDate={originalDate}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// 操作按鈕組件
interface ActionButtonsProps {
  entry: TimeEntry
  onEdit?: (entry: TimeEntry, originalDate?: string) => void
  onDelete?: (entryId: string) => void
  onStop?: () => void
  isSubmitted?: boolean
  originalDate?: string
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  entry,
  onEdit,
  onDelete,
  onStop,
  isSubmitted = false,
  originalDate
}) => {
  const [showDropdown, setShowDropdown] = useState(false)
  const isRunning = entry.status === 'RUNNING'

  if (isSubmitted) return null

  return (
    <div className="flex items-center gap-1">
      {/* 桌面版：直接顯示按鈕 */}
      <div className="hidden md:flex items-center gap-1">
        {isRunning ? (
          onStop && (
            <Tooltip content="停止計時">
              <button
                onClick={onStop}
                className="p-2 rounded-lg text-red-500 hover:text-red-600 
                  hover:bg-red-50 transition-colors"
              >
                <StopCircle className="w-4 h-4" />
              </button>
            </Tooltip>
          )
        ) : (
          <>
            {onEdit && (
              <Tooltip content="編輯記錄">
                <button
                  onClick={() => onEdit(entry, originalDate)}
                  className="p-2 rounded-lg text-secondary-400 hover:text-primary-600 
                    hover:bg-primary-50 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip content="刪除記錄">
                <button
                  onClick={() => onDelete(entry.id)}
                  className="p-2 rounded-lg text-secondary-400 hover:text-red-600 
                    hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </Tooltip>
            )}
          </>
        )}
      </div>

      {/* 手機版：下拉選單 */}
      <div className="md:hidden relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="p-2 rounded-lg text-secondary-400 hover:text-secondary-600 
            hover:bg-secondary-100 transition-colors"
        >
          <MoreVertical className="w-4 h-4" />
        </button>

        {showDropdown && (
          <div className="absolute right-0 top-10 w-36 glass-card rounded-xl 
            shadow-lg border border-secondary-200/50 py-1.5 z-20 animate-fade-in">
            {isRunning ? (
              onStop && (
                <button
                  onClick={() => {
                    onStop()
                    setShowDropdown(false)
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm 
                    text-red-600 hover:bg-red-50 transition-colors"
                >
                  <StopCircle className="w-4 h-4" />
                  停止計時
                </button>
              )
            ) : (
              <>
                {onEdit && (
                  <button
                    onClick={() => {
                      onEdit(entry, originalDate)
                      setShowDropdown(false)
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm 
                      text-secondary-700 hover:bg-secondary-50 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    編輯記錄
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => {
                      onDelete(entry.id)
                      setShowDropdown(false)
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm 
                      text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    刪除記錄
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default WeeklyTimeEntryList
